import { BigDecimal, BigDecimals } from '@vertex-protocol/client';
import { getMarketSizeFormatSpecifier } from '@vertex-protocol/react-client';
import { useRunWithDelayOnCondition } from 'client/hooks/util/useRunWithDelayOnCondition';
import {
  TpSlPlaceOrderFormPositionChanges,
  UseTpSlPlaceOrderForm,
} from 'client/modules/trading/tpsl/hooks/useTpSlPlaceOrderForm/types';
import { useTpSlPlaceOrderForm } from 'client/modules/trading/tpsl/hooks/useTpSlPlaceOrderForm/useTpSlPlaceOrderForm';
import { useTpSlPositionData } from 'client/modules/trading/tpsl/tpslDialog/hooks/useTpSlPositionData';
import { BaseActionButtonState } from 'client/types/BaseActionButtonState';
import { useMemo } from 'react';

export interface UseTpSlDialogOrderForm
  extends Omit<UseTpSlPlaceOrderForm, 'executePlaceOrder'> {
  marketName: string | undefined;
  positionSize: BigDecimal | undefined;
  sizeFormatSpecifier: string;
  buttonState: BaseActionButtonState;
}

interface Params {
  productId: number;
  isoSubaccountName: string | null;
  isTakeProfit: boolean;
}

export function useTpSlDialogOrderForm({
  productId,
  isTakeProfit,
  isoSubaccountName,
}: Params): UseTpSlDialogOrderForm {
  const tpSlPositionData = useTpSlPositionData({
    productId,
    isoSubaccountName,
  });
  const marketName = tpSlPositionData?.metadata?.marketName;
  const priceIncrement = tpSlPositionData?.priceIncrement;
  const lastPrice = tpSlPositionData?.lastPrice;
  const oraclePrice = tpSlPositionData?.fastOraclePrice;
  const positionSize = tpSlPositionData?.amount.abs();
  const longWeightInitial = tpSlPositionData?.longWeightInitial;
  const sizeFormatSpecifier = getMarketSizeFormatSpecifier(
    tpSlPositionData?.sizeIncrement,
  );

  // Pass zero-delta positionChanges so the estimated state hook uses the
  // existing position data for PnL calculation and price/percentage sync.
  // Without this, positionAmount and positionNetEntry default to ZERO,
  // making the estimated PnL always $0.00 and breaking bidirectional sync.
  const positionChanges = useMemo(
    (): TpSlPlaceOrderFormPositionChanges | undefined => {
      if (!tpSlPositionData) return undefined;
      return {
        assetAmountDelta: BigDecimals.ZERO,
        netEntryAmountDelta: BigDecimals.ZERO,
      };
    },
    [tpSlPositionData],
  );

  const { executePlaceOrder, ...tpSlPlaceOrderform } = useTpSlPlaceOrderForm({
    priceIncrement,
    lastPrice,
    oraclePrice,
    productId,
    isTakeProfit,
    longWeightInitial,
    isoSubaccountName,
    positionChanges,
  });

  useRunWithDelayOnCondition({
    condition: executePlaceOrder.isSuccess,
    fn: executePlaceOrder.reset,
  });

  const buttonState = useMemo((): BaseActionButtonState => {
    const { formError, validTriggerPrice, referencePrice } = tpSlPlaceOrderform;

    if (executePlaceOrder.isPending) {
      return 'loading';
    } else if (executePlaceOrder.isSuccess) {
      return 'success';
    } else if (formError || !validTriggerPrice || !referencePrice) {
      return 'disabled';
    } else {
      return 'idle';
    }
  }, [executePlaceOrder, tpSlPlaceOrderform]);

  return {
    ...tpSlPlaceOrderform,
    marketName,
    positionSize,
    sizeFormatSpecifier,
    buttonState,
  };
}
